import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Code, Group, Stack, Text, Textarea, Title } from '@mantine/core';
import {
  useAppendSuiteBlueprint,
  usePreviewSuiteBlueprint,
  useSuiteBlueprint,
  useSuiteHistory,
  useSuiteRevision,
} from '@/hooks/api/automationSuites/useAutomationSuites.ts';
import {
  suiteDraftKey,
  useSuiteDrafts,
  type SuiteDraft,
} from '@/context/SuiteDraftContext/SuiteDraftContext.ts';
import { useOrgProject } from '@/context/OrgProjectContext/useOrgProject.ts';
import { blueprintTextState } from '@/utils/blueprintText.ts';
import type {
  SuiteBlueprintCurrent,
  SuiteBlueprintPreview,
  SuiteBlueprintProposal,
  SuiteDetail,
} from '@/types/automationSuites.ts';
import {
  SuiteError,
  SuiteMarkdown,
  SuitePagination,
} from '@/pages/AutomationSuites/SuiteShared.tsx';

import { suiteProposal } from '@/utils/suiteBlueprint.ts';
import { formatCalendarStyle } from '@/utils/datetime.ts';

const errorStatus = (error: unknown) =>
  (error as { response?: { status?: number } } | null)?.response?.status;

function fromCurrent(current: SuiteBlueprintCurrent): SuiteDraft {
  const body = current.revision?.body ?? '';
  return {
    body,
    originalBody: body,
    head: current.head_revision,
    revisionId: current.revision?.id ?? null,
  };
}

export default function SuiteBlueprint({
  suite,
  canEdit,
  refresh,
}: {
  suite: SuiteDetail;
  canEdit: boolean;
  refresh: () => void;
}) {
  const { orgId } = useOrgProject();
  const store = useSuiteDrafts();
  const key = suiteDraftKey(orgId, suite.project_id, suite.id);
  const current = useSuiteBlueprint(suite.id, suite.shared_text_available);
  const [draft, setDraft] = useState<SuiteDraft | null>(null);
  const [preview, setPreview] = useState<{
    receipt: SuiteBlueprintPreview;
    proposal: SuiteBlueprintProposal;
  } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [conflict, setConflict] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [selectedRevision, setSelectedRevision] = useState<string | null>(null);
  const [discardConfirmation, setDiscardConfirmation] = useState(false);
  const [authorityBlocked, setAuthorityBlocked] = useState(false);
  const history = useSuiteHistory(
    suite.id,
    historyPage,
    showHistory && suite.shared_text_available,
  );
  const historical = useSuiteRevision(suite.id, selectedRevision);
  const previewMutation = usePreviewSuiteBlueprint();
  const saveMutation = useAppendSuiteBlueprint();
  const generation = useRef(0);
  const mounted = useRef(true);
  const authority = useRef({ canEdit, head: suite.head_revision });
  authority.current = {
    canEdit: canEdit && suite.shared_text_available && !authorityBlocked,
    head: suite.head_revision,
  };
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (
      !draft &&
      current.data &&
      current.isFetchedAfterMount &&
      !current.isFetching &&
      !current.isError &&
      suite.shared_text_available &&
      !authorityBlocked
    ) {
      setDraft(store.drafts[key] ?? fromCurrent(current.data));
    } else if (
      draft &&
      draft.body === draft.originalBody &&
      !preview &&
      !conflict &&
      current.data &&
      !current.isError &&
      suite.shared_text_available &&
      !authorityBlocked &&
      current.data.head_revision === suite.head_revision &&
      (draft.head !== current.data.head_revision ||
        draft.revisionId !== (current.data.revision?.id ?? null))
    ) {
      // Clean views may follow a fresh authoritative read. Dirty or previewed
      // proposals always keep their original basis for explicit reconciliation.
      setDraft(fromCurrent(current.data));
    }
  }, [
    current.data,
    current.isFetchedAfterMount,
    current.isFetching,
    current.isError,
    draft,
    key,
    store.drafts,
    suite.shared_text_available,
    suite.head_revision,
    authorityBlocked,
    preview,
    conflict,
  ]);
  const clearDraft = store.clear;
  const permissionFailure = [current.error, history.error, historical.error].some(
    (failure) => errorStatus(failure) === 403,
  );
  useEffect(() => {
    if (!suite.shared_text_available || permissionFailure || errorStatus(current.error) === 404) {
      clearDraft(key);
      setDraft(null);
      setPreview(null);
      if (permissionFailure) setAuthorityBlocked(true);
      generation.current++;
    }
  }, [suite.shared_text_available, current.error, permissionFailure, key, clearDraft]);

  if (!suite.shared_text_available)
    return (
      <Alert color="orange" title="Blueprint access limited">
        Shared prose is unavailable in this authorized scope.
      </Alert>
    );
  const retryAuthority = async () => {
    const results = await Promise.all([
      current.refetch(),
      ...(showHistory ? [history.refetch()] : []),
      ...(selectedRevision ? [historical.refetch()] : []),
    ]);
    refresh();
    if (results.every((result) => result.isSuccess)) setAuthorityBlocked(false);
  };
  if (permissionFailure || authorityBlocked)
    return (
      <Alert color="orange" title="Blueprint authority changed">
        Shared text and the retained proposal have been cleared. Refresh Suite access before
        continuing.
        <Button variant="default" onClick={() => void retryAuthority()}>
          Refresh access
        </Button>
      </Alert>
    );
  if (current.isError)
    return <SuiteError error={current.error} retry={() => void current.refetch()} />;
  if (!current.data || !draft) return <Text>Loading blueprint…</Text>;
  const latest = current.data;
  const stale =
    conflict ||
    draft.head !== suite.head_revision ||
    draft.head !== latest.head_revision ||
    draft.revisionId !== (latest.revision?.id ?? null);
  const dirty = draft.body !== draft.originalBody;
  const busy = previewMutation.isPending || saveMutation.isPending;
  const state = blueprintTextState(draft.body);
  const editable = canEdit && !stale && !current.isFetching && !busy;

  const change = (body: string) => {
    const next = { ...draft, body };
    generation.current++;
    setDraft(next);
    store.put(key, next);
    setPreview(null);
    setError(null);
  };
  const loadLatest = (keep: boolean) => {
    if (latest.head_revision !== suite.head_revision || current.isFetching) return;
    const next = fromCurrent(latest);
    if (keep) next.body = draft.body;
    generation.current++;
    setDraft(next);
    store.put(key, next);
    setPreview(null);
    setConflict(false);
    setError(null);
    setDiscardConfirmation(false);
  };
  const failed = (failure: unknown) => {
    const denied = [403, 404].includes(errorStatus(failure) ?? 0);
    // Revocation applies to retained text even after this editor unmounts.
    if (denied) store.clear(key);
    if (!mounted.current) return;
    setError(failure);
    if ([409, 412].includes(errorStatus(failure) ?? 0)) {
      setConflict(true);
      setPreview(null);
      void current.refetch();
      refresh();
    }
    if (denied) {
      generation.current++;
      setAuthorityBlocked(true);
      setDraft(null);
      setPreview(null);
      void current.refetch();
      refresh();
    }
  };
  const prepare = async () => {
    if (!editable || state.error) return;
    const attempt = ++generation.current;
    setError(null);
    try {
      const proposal = await suiteProposal(draft);
      if (
        !mounted.current ||
        attempt !== generation.current ||
        !authority.current.canEdit ||
        authority.current.head !== draft.head
      )
        return;
      const receipt = await previewMutation.mutateAsync({
        orgId,
        projectId: suite.project_id,
        suiteId: suite.id,
        proposal,
      });
      if (mounted.current && attempt === generation.current) setPreview({ receipt, proposal });
    } catch (failure) {
      failed(failure);
    }
  };
  const save = async () => {
    if (!preview || !editable) return;
    const attempt = generation.current;
    try {
      const saved = await saveMutation.mutateAsync({
        orgId,
        projectId: suite.project_id,
        suiteId: suite.id,
        proposal: preview.proposal,
      });
      // A completed save still clears its original proposal after internal navigation,
      // but must not erase a newer draft started on another mounted view.
      store.clearMatching(key, draft);
      if (mounted.current && attempt === generation.current) {
        setDraft(fromCurrent(saved));
        setPreview(null);
        setConflict(false);
        setError(null);
        refresh();
      }
    } catch (failure) {
      failed(failure);
    }
  };
  const download = () => {
    const url = URL.createObjectURL(
      new Blob([draft.body], { type: 'text/markdown;charset=utf-8' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = 'suite-blueprint-draft.md';
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Suite blueprint</Title>
        <Button variant="default" onClick={() => setShowHistory(!showHistory)}>
          {showHistory ? 'Hide history' : 'Revision history'}
        </Button>
      </Group>
      <Text size="sm" c="dimmed">
        Working Suite documentation. Releases retain their own selected revisions. Shared intent,
        policy ownership, handoffs and evidence plan. Local scripts may be referenced here; their
        receipts are local proof, not verified V2 assurance.
      </Text>
      <Text size="sm">
        {latest.revision
          ? `Saved by ${latest.revision.created_by.display_name || latest.revision.created_by.id} · ${formatCalendarStyle(latest.revision.created_at)}`
          : 'No saved blueprint yet.'}{' '}
        Saving creates an immutable revision; it does not approve or publish anything.
      </Text>
      {!latest.revision && latest.starter_blueprint && !dirty && canEdit && (
        <Alert color="blue" title="Start a blueprint">
          <Text size="sm">
            Use the starter to describe the shared design. It remains an unsaved local draft until
            you preview and save it.
          </Text>
          <Button
            variant="subtle"
            disabled={!editable}
            onClick={() => change(latest.starter_blueprint!.body)}
          >
            Start blueprint
          </Button>
        </Alert>
      )}
      {stale && (
        <Alert color="orange" title="Suite changed — your draft is retained">
          <Stack gap="xs">
            <Text size="sm">
              Review the latest saved text before continuing. Metadata or membership changes also
              invalidate a proposal.
            </Text>
            <Button
              variant="default"
              onClick={() => {
                void current.refetch();
                refresh();
              }}
            >
              Refresh latest
            </Button>
            <Group>
              <Button
                disabled={
                  busy ||
                  current.isFetching ||
                  latest.head_revision !== suite.head_revision ||
                  !canEdit
                }
                onClick={() => loadLatest(true)}
              >
                Keep my text on latest basis
              </Button>
              <Button
                variant="default"
                disabled={
                  busy || current.isFetching || latest.head_revision !== suite.head_revision
                }
                onClick={() => setDiscardConfirmation(true)}
              >
                Discard draft and load latest
              </Button>
            </Group>
            <Text fw={600}>Latest saved text</Text>
            <SuiteMarkdown body={latest.revision?.body ?? 'No saved document.'} />
          </Stack>
        </Alert>
      )}
      {error !== null && <SuiteError error={error} />}
      {canEdit ? (
        <Textarea
          label="Suite blueprint Markdown"
          rows={16}
          styles={{ input: { resize: 'vertical' } }}
          value={draft.body}
          disabled={busy}
          onChange={(event) => change(event.currentTarget.value)}
        />
      ) : (
        <SuiteMarkdown body={latest.revision?.body ?? 'No saved document.'} />
      )}
      {!canEdit && dirty && (
        <>
          <Text c="orange">
            An unsaved proposal is retained. This Suite is currently read-only.
          </Text>
          <SuiteMarkdown body={draft.body} />
        </>
      )}
      {canEdit && (
        <Text size="sm" c={state.error ? 'red' : 'dimmed'}>
          {state.error?.replaceAll('Version blueprint', 'Suite blueprint') ??
            `${state.bytes.toLocaleString()} UTF-8 bytes${dirty ? ' · unsaved draft' : ''}`}
        </Text>
      )}
      <Group>
        {canEdit && (
          <>
            <Button
              disabled={!editable || !!state.error}
              loading={previewMutation.isPending}
              onClick={() => void prepare()}
            >
              Preview proposal
            </Button>
            <Button
              disabled={!preview || !editable}
              loading={saveMutation.isPending}
              onClick={() => void save()}
            >
              Save blueprint
            </Button>
          </>
        )}
        {dirty && (
          <>
            <Button variant="default" onClick={download}>
              Download draft
            </Button>
            <Button variant="default" disabled={busy} onClick={() => setDiscardConfirmation(true)}>
              Discard draft
            </Button>
          </>
        )}
      </Group>
      {discardConfirmation && (
        <Alert color="orange" title="Discard unsaved text?">
          <Group>
            <Button
              disabled={current.isFetching || busy || latest.head_revision !== suite.head_revision}
              onClick={() => loadLatest(false)}
            >
              Confirm discard
            </Button>
            <Button variant="default" onClick={() => setDiscardConfirmation(false)}>
              Keep editing
            </Button>
          </Group>
        </Alert>
      )}
      {preview && !stale && (
        <>
          <Title order={3}>Proposed Markdown</Title>
          <SuiteMarkdown body={preview.proposal.body} />
          {preview.receipt.diff_truncated && (
            <Alert color="orange">
              Diff excerpt is truncated. Review the complete proposed Markdown above; an empty
              excerpt does not mean no changes.
            </Alert>
          )}
          <Code block style={{ whiteSpace: 'pre-wrap' }}>
            {preview.receipt.diff_excerpt.join('\n') || 'No changes in the displayed excerpt.'}
          </Code>
        </>
      )}
      {showHistory && (
        <>
          <Title order={3}>Immutable history</Title>
          {!history.isError && !history.isFetchedAfterMount && (
            <Text>Loading revision history…</Text>
          )}
          {history.isError && (
            <SuiteError error={history.error} retry={() => void history.refetch()} />
          )}
          {!history.isError && history.isFetchedAfterMount && history.data && (
            <>
              {!history.data.results.length && <Text>No saved revisions.</Text>}
              {history.data.results.map((revision) => (
                <Group key={revision.id}>
                  <Text size="sm">
                    {formatCalendarStyle(revision.created_at)} ·{' '}
                    {revision.created_by.display_name || revision.created_by.id} ·{' '}
                    {revision.source_kind}
                  </Text>
                  <Button
                    variant="default"
                    size="xs"
                    onClick={() => setSelectedRevision(revision.id)}
                  >
                    View revision {revision.id}
                  </Button>
                </Group>
              ))}
              <SuitePagination data={history.data} onPage={setHistoryPage} />
            </>
          )}
          {selectedRevision && (
            <>
              <Text fw={600}>Historical revision · read-only</Text>
              {!historical.isError && !historical.isFetchedAfterMount && (
                <Text>Loading historical text…</Text>
              )}
              {historical.isError && (
                <SuiteError error={historical.error} retry={() => void historical.refetch()} />
              )}
              {!historical.isError && historical.isFetchedAfterMount && historical.data && (
                <SuiteMarkdown body={historical.data.body} />
              )}
              <Button variant="default" onClick={() => setSelectedRevision(null)}>
                Close historical text
              </Button>
            </>
          )}
        </>
      )}
    </Stack>
  );
}
